/**
 * 七巧板核心逻辑(与界面无关,便于测试)
 *
 * 坐标:以网格为单位,y 轴向下(与 SVG 一致)。7 块板都取"顶点落在整数网格上"
 * 的摆法,只允许 90° 旋转(平行四边形另可翻面),这样:
 *   - 拖动松手后吸附到整数网格,孩子在手机上容易对准
 *   - 每块板都能精确拆成"四分之一格":每个单位格被两条对角线切成上右下左
 *     4 个小三角,板子覆盖的正是其中若干个 —— 判定拼对只需比较集合,零误差
 *
 * 随机出题:把 7 块板一块块贴着已放好的部分接上去(优先贴得紧的位置),
 * 拼出的轮廓就是题目 —— 剪影本身由这 7 块拼成,所以每道题一定有解。
 */

export const PIECES = [
  { id: 'L1', kind: 'L', name: '大三角', color: '#D9776B', verts: [[0, 0], [4, 0], [2, 2]] },
  { id: 'L2', kind: 'L', name: '大三角', color: '#6AA4B5', verts: [[0, 0], [4, 0], [2, 2]] },
  { id: 'M', kind: 'M', name: '中三角', color: '#F0C75E', verts: [[0, 0], [2, 0], [0, 2]] },
  { id: 'SQ', kind: 'SQ', name: '正方形', color: '#DDA24E', verts: [[1, 0], [2, 1], [1, 2], [0, 1]] },
  { id: 'S1', kind: 'S', name: '小三角', color: '#9686C2', verts: [[0, 0], [2, 0], [1, 1]] },
  { id: 'S2', kind: 'S', name: '小三角', color: '#7FAE86', verts: [[0, 0], [2, 0], [1, 1]] },
  { id: 'P', kind: 'P', name: '平行四边形', color: '#A8D5C6', verts: [[0, 0], [2, 0], [3, 1], [1, 1]] },
];

export const PIECE_BY_ID = Object.fromEntries(PIECES.map((p) => [p.id, p]));

/** 平行四边形翻面后不能靠旋转还原,其余形状翻面等同于某个旋转 */
export const canFlip = (id) => PIECE_BY_ID[id].kind === 'P';

// ---------- 几何 ----------

function normalize(verts) {
  const minX = Math.min(...verts.map((v) => v[0]));
  const minY = Math.min(...verts.map((v) => v[1]));
  return verts.map(([x, y]) => [x - minX, y - minY]);
}

/** 按朝向取顶点:先翻面(左右镜像),再顺时针转 rot 次 90°;结果平移到最小坐标为 0 */
export function orient(id, { rot, flip }) {
  let vs = PIECE_BY_ID[id].verts.map(([x, y]) => [flip ? -x : x, y]);
  for (let i = 0; i < rot; i += 1) vs = vs.map(([x, y]) => [-y, x]);
  return normalize(vs);
}

export function sizeOf(verts) {
  return {
    w: Math.max(...verts.map((v) => v[0])),
    h: Math.max(...verts.map((v) => v[1])),
  };
}

function pointInPolygon([px, py], verts) {
  let inside = false;
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i, i += 1) {
    const [xi, yi] = verts[i];
    const [xj, yj] = verts[j];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

// 四分之一格 q:0 贴上边、1 贴右边、2 贴下边、3 贴左边;取各自重心判断是否在板内
const QUARTER_CENTER = [
  [0.5, 0.25],
  [0.75, 0.5],
  [0.5, 0.75],
  [0.25, 0.5],
];

const key = (cx, cy, q) => `${cx},${cy},${q}`;

/** 一块板放在 (x, y)(整数)时覆盖的四分之一格 */
export function quartersOf(verts, x, y) {
  const { w, h } = sizeOf(verts);
  const out = [];
  for (let cx = 0; cx < w; cx += 1) {
    for (let cy = 0; cy < h; cy += 1) {
      for (let q = 0; q < 4; q += 1) {
        const [ox, oy] = QUARTER_CENTER[q];
        if (pointInPolygon([cx + ox, cy + oy], verts)) out.push(key(cx + x, cy + y, q));
      }
    }
  }
  return out;
}

/** 与某个四分之一格共边的 3 个四分之一格 */
function neighbors(k) {
  const [cx, cy, q] = k.split(',').map(Number);
  const across = [
    [cx, cy - 1, 2],
    [cx + 1, cy, 3],
    [cx, cy + 1, 0],
    [cx - 1, cy, 1],
  ][q];
  return [key(cx, cy, (q + 1) % 4), key(cx, cy, (q + 3) % 4), key(...across)];
}

// ---------- 朝向 ----------

function shapeSignature(id, state) {
  return quartersOf(orient(id, state), 0, 0).sort().join('|');
}

/** 一块板所有互不相同的朝向(正方形只有 1 种,三角形 4 种,平行四边形 4 种:翻面等于转 180° 后的镜像) */
export function distinctStates(id) {
  const seen = new Map();
  const flips = canFlip(id) ? [false, true] : [false];
  flips.forEach((flip) => {
    for (let rot = 0; rot < 4; rot += 1) {
      const sig = shapeSignature(id, { rot, flip });
      if (!seen.has(sig)) seen.set(sig, { rot, flip });
    }
  });
  return [...seen.values()];
}

/** 从 from 朝向转到和 to 形状一样,最少要几步(转一次、翻一次各算一步) */
export function rotationDistance(id, from, to) {
  const target = shapeSignature(id, to);
  let frontier = [from];
  const seen = new Set([`${from.rot}${from.flip}`]);
  for (let steps = 0; steps <= 5; steps += 1) {
    if (frontier.some((s) => shapeSignature(id, s) === target)) return steps;
    const next = [];
    frontier.forEach((s) => {
      const moves = [{ rot: (s.rot + 1) % 4, flip: s.flip }];
      if (canFlip(id)) moves.push({ rot: s.rot, flip: !s.flip });
      moves.forEach((m) => {
        const k = `${m.rot}${m.flip}`;
        if (!seen.has(k)) {
          seen.add(k);
          next.push(m);
        }
      });
    });
    frontier = next;
  }
  return Infinity;
}

// ---------- 随机出题 ----------

/** 可复现的随机数(同一个 seed 出同一道题,方便测试和"重来这一题") */
export function makeRng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function bboxOf(keys) {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  keys.forEach((k) => {
    const [cx, cy] = k.split(',').map(Number);
    x0 = Math.min(x0, cx);
    y0 = Math.min(y0, cy);
    x1 = Math.max(x1, cx + 1);
    y1 = Math.max(y1, cy + 1);
  });
  return { x0, y0, x1, y1 };
}

/**
 * 随机拼一个图形。返回 { solution: [{ id, state, x, y }], target: Set<四分之一格> },
 * 已平移到 board(宽 boardW、高 boardH)正中。
 */
export function generateShape(seed, { maxSize = 8, boardW = 11, boardH = 9 } = {}) {
  const rng = makeRng(seed);
  const order = PIECES.map((p) => p.id);
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  const occupied = new Set();
  const placed = [];

  order.forEach((id, i) => {
    const states = distinctStates(id);
    if (i === 0) {
      const state = states[Math.floor(rng() * states.length)];
      quartersOf(orient(id, state), 0, 0).forEach((k) => occupied.add(k));
      placed.push({ id, state, x: 0, y: 0 });
      return;
    }
    const box = bboxOf(occupied);
    const candidates = [];
    states.forEach((state) => {
      const vs = orient(id, state);
      const { w, h } = sizeOf(vs);
      for (let x = box.x0 - w; x <= box.x1; x += 1) {
        for (let y = box.y0 - h; y <= box.y1; y += 1) {
          const qs = quartersOf(vs, x, y);
          if (qs.some((k) => occupied.has(k))) continue;
          const contact = qs.reduce(
            (n, k) => n + neighbors(k).filter((nb) => occupied.has(nb)).length,
            0,
          );
          // 至少贴合两条小边,避免只靠一个角挂着
          if (contact < 2) continue;
          const nb = bboxOf([...occupied, ...qs]);
          if (nb.x1 - nb.x0 > maxSize || nb.y1 - nb.y0 > maxSize) continue;
          candidates.push({ state, x, y, qs, contact });
        }
      }
    });
    // 贴得越紧越可能被选中(平方加权),拼出成块的形状,又不每次都一样
    const total = candidates.reduce((s, c) => s + c.contact ** 2, 0);
    let r = rng() * total;
    const pick = candidates.find((c) => {
      r -= c.contact ** 2;
      return r <= 0;
    }) || candidates[candidates.length - 1];
    pick.qs.forEach((k) => occupied.add(k));
    placed.push({ id, state: pick.state, x: pick.x, y: pick.y });
  });

  // 平移到板子正中
  const box = bboxOf(occupied);
  const dx = Math.floor((boardW - (box.x1 - box.x0)) / 2) - box.x0;
  const dy = Math.floor((boardH - (box.y1 - box.y0)) / 2) - box.y0;
  const solution = placed.map((p) => ({ ...p, x: p.x + dx, y: p.y + dy }));
  const target = new Set();
  solution.forEach((p) => quartersOf(orient(p.id, p.state), p.x, p.y).forEach((k) => target.add(k)));
  return { solution, target };
}

// ---------- 判定 ----------

/**
 * 板上的摆法是否正好拼出目标:每块都在板上、互不重叠、覆盖的格子和目标完全一样。
 * 不要求和出题时的摆法一致 —— 轮廓对上就算对。
 */
export function isSolved(pieces, target) {
  if (!pieces.every((p) => p.onBoard)) return false;
  const covered = new Set();
  let count = 0;
  pieces.forEach((p) => {
    quartersOf(orient(p.id, p.state), p.x, p.y).forEach((k) => {
      covered.add(k);
      count += 1;
    });
  });
  if (count !== covered.size || covered.size !== target.size) return false;
  return [...target].every((k) => covered.has(k));
}

/** 本题的步数上限:每块至少拖一次 + 从初始朝向转到答案朝向的最少步数 + 难度余量 */
export function stepLimit(solution, trayStates, slack) {
  const turns = solution.reduce(
    (n, p) => n + rotationDistance(p.id, trayStates[p.id], p.state),
    0,
  );
  return PIECES.length + turns + slack;
}
